import React, { useState, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { BOOK_CATEGORIES, STATUS_PILLS } from './bookListUtils.js';

interface BookFilterToolbarProps {
  selectedCategory: string;
  onSelectCategory: (category: string) => void;
  selectedStatus: string;
  onSelectStatus: (status: string) => void;
}

export const BookFilterToolbar: React.FC<BookFilterToolbarProps> = ({
  selectedCategory,
  onSelectCategory,
  selectedStatus,
  onSelectStatus,
}) => {
  const [isCategoryFilterOpen, setIsCategoryFilterOpen] = useState<boolean>(false);

  useEffect(() => {
    const handleCategoryOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.category-filter-dropdown-container')) {
        setIsCategoryFilterOpen(false);
      }
    };
    if (isCategoryFilterOpen) {
      document.addEventListener('mousedown', handleCategoryOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleCategoryOutsideClick);
    };
  }, [isCategoryFilterOpen]);

  return (
    <>
      {/* Category dropdown */}
      <div className="w-full md:w-56 shrink-0 relative category-filter-dropdown-container">
        <button
          id="btn-category-filter-dropdown"
          type="button"
          onClick={() => setIsCategoryFilterOpen(!isCategoryFilterOpen)}
          className="w-full min-h-[44px] flex items-center justify-between px-3.5 py-2 text-xs sm:text-sm border border-slate-200 rounded-xl bg-white hover:bg-slate-50 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 cursor-pointer text-left transition duration-150"
        >
          <span className="text-slate-700 truncate font-medium">
            {selectedCategory === 'all' ? 'Semua Kategori' : selectedCategory}
          </span>
          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isCategoryFilterOpen ? 'rotate-180' : ''}`} />
        </button>

        {isCategoryFilterOpen && (
          <div className="absolute z-50 w-full mt-1.5 bg-white border border-slate-200 rounded-xl shadow-lg max-h-64 overflow-y-auto overflow-x-hidden animate-in fade-in slide-in-from-top-1 duration-100">
            <div className="p-1.5 space-y-0.5">
              {BOOK_CATEGORIES.map((cat, idx) => {
                const isSelected = selectedCategory === cat.id;
                return (
                  <button
                    key={`${cat.id}-${idx}`}
                    type="button"
                    onClick={() => {
                      onSelectCategory(cat.id);
                      setIsCategoryFilterOpen(false);
                    }}
                    className={`w-full min-h-[44px] flex items-center justify-between px-3 py-2 text-xs font-medium rounded-lg text-left transition duration-150 cursor-pointer ${
                      isSelected
                        ? 'bg-indigo-50 text-indigo-700'
                        : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <span>{cat.name}</span>
                    {isSelected && <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Status filter pills */}
      <div className="w-full flex items-center gap-1.5 overflow-x-auto pt-1 pb-0.5 text-xs">
        <span className="text-slate-400 text-[11px] font-medium mr-1 shrink-0">Status:</span>
        {STATUS_PILLS.map((p, idx) => (
          <button
            key={`${p.id}-${idx}`}
            onClick={() => onSelectStatus(p.id)}
            className={`px-3 py-1 rounded-lg font-medium transition whitespace-nowrap min-h-[44px] flex items-center justify-center shrink-0 ${
              selectedStatus === p.id
                ? 'bg-slate-900 text-white shadow-2xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
    </>
  );
};
